import {
  InvitationMessage,
  InvitationMessageType,
  generateInvitationMessage as generateBaseInvitationMessage,
  InvitationQueryPayloadResponse,
  InvitationIsAliasPayload,
} from '@playcastdotio/Messaging';
import { sendMessage } from '..';
import { getPersistentPlayerId } from '../../..';
import { onInviteQueryResponse, onAlias, onAliasUpdate, connectPeer } from '../../player';
import { invitationState, lobbyState } from '../../../stores/lobbyStores';
import { IdentityPlatform } from '@playcastdotio/CoreTypes';
import { joinState } from '../../../stores/globalStores';
import { invitationDiagnosticsCache } from '../invitationDiagnosticsCache';

// Track invitation IDs that have already had join requests sent to prevent duplicates
const sentJoinRequests = new Set<string>();

// Export function to clear join request tracking (e.g., for retry scenarios)
export const clearJoinRequest = (invitationId: string) => {
  sentJoinRequests.delete(invitationId);
};

export const resetAllJoinRequests = () => {
  sentJoinRequests.clear();
};

export const handleInvitationMessage = (message: InvitationMessage) => {
  console.log('Invitation message:', message);
  switch (message.type) {
    case InvitationMessageType.INVITATION_QUERY_RESPONSE: {
      console.warn('Invitation query response');
      const queryResponse = message as InvitationMessage & InvitationQueryPayloadResponse;

      // Cache the invitation for diagnostics if it exists
      if (queryResponse.invitation) {
        console.log('Caching invitation from query response for diagnostics:', queryResponse.invitation.id);
        invitationDiagnosticsCache.set(queryResponse.invitation.id, queryResponse.invitation, 'updated');
      }

      onInviteQueryResponse(
        queryResponse.invitationId,
        queryResponse.invitation,
        queryResponse.defaultAlias,
        queryResponse.defaultAliasPlatform,
      );
      break;
    }
    case InvitationMessageType.INVITATION_IS_ALIAS: {
      console.warn('Invitation is alias');
      const aliasMessage = message as InvitationMessage & InvitationIsAliasPayload;
      invitationState.updateState({
        ...invitationState.currentState,
        isAlias: true,
      });
      // Get the default alias from the message
      const defaultAlias = aliasMessage.defaultAlias;
      onAlias(aliasMessage.invitations, defaultAlias || '', '');
      break;
    }
    case InvitationMessageType.INVITATION_ALIAS_CHANGED: {
      console.warn('Invitation alias changed');
      onAliasUpdate(message.updatedInvitation);
      break;
    }
    case InvitationMessageType.INVITATION_JOIN_SUCCESS: {
      console.warn('Invitation join success');
      const position = message.position;
      invitationState.updateState({
        ...invitationState.currentState,
        invitationPlayerId: message.invitationPlayerId,
      });

      lobbyState.updateState({
        ...lobbyState.currentState,
        position,
      });

      if (message.hostPeerId) {
        connectPeer(message.hostPeerId);
        // Don't update state if already connected
        if (joinState.currentState.state !== 'hostConnected') {
          joinState.updateState({
            ...joinState.currentState,
            state: 'connecting',
          });
        }
      }
      break;
    }
    case InvitationMessageType.INVITATION_JOIN_ERROR: {
      console.warn('Invitation join err');
      // If the message is "Already in lobby", ignore it.
      if (message.error.message === 'Already in lobby') {
        break;
      }
      // If we're currently connecting and get a join error, show error state
      if (joinState.currentState.state === 'connecting') {
        joinState.updateState({
          ...joinState.currentState,
          state: 'error',
        });
      }
      break;
    }
    case InvitationMessageType.INVITATION_QUERY:
    case InvitationMessageType.INVITATION_JOIN:
    case InvitationMessageType.INVITATION_STORE:
    case InvitationMessageType.INVITATION_STORE_ERROR:
    case InvitationMessageType.INVITATION_STORE_SUCCESS: {
      console.warn('Server received message it should not have received');
      console.log(message);
      break;
    }
  }
};

export const queryInvitation = (invitationId: string, platform: IdentityPlatform) => {
  sendMessage(
    generateBaseInvitationMessage(InvitationMessageType.INVITATION_QUERY, {
      invitationId,
      platform,
    }),
  );
};

export const requestJoinInvitation = (invitationId: string, displayName: string) => {
  // Prevent duplicate join requests for the same invitation
  if (sentJoinRequests.has(invitationId)) {
    console.log('Join request already sent for invitation:', invitationId, 'skipping duplicate');
    return;
  }

  // Mark this invitation as having a join request sent
  sentJoinRequests.add(invitationId);

  console.log('Sending INVITATION_JOIN for invitation:', invitationId);
  sendMessage(
    generateBaseInvitationMessage(InvitationMessageType.INVITATION_JOIN, {
      invitationId,
      displayName,
      persistentId: getPersistentPlayerId(),
    }),
  );
};

export const generateInvitationMessage = (
  type: InvitationMessageType,
  payload: {
    invitationId: string;
    platform?: IdentityPlatform;
  },
): InvitationMessage => {
  return generateBaseInvitationMessage(type, payload);
};
